import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import trainingService from "./trainingService";

interface TrainingState {
    initiateTraining: {
        isLoading: boolean;
        isSuccess: boolean;
        isError: boolean;
        message: string;
    };
    fetchTrainingData: {
        trainingData: any;
        isLoading: boolean;
        isSuccess: boolean;
        isError: boolean;
        message: string;
    };
    deleteTraining: {
        isLoading: boolean;
        isSuccess: boolean;
        isError: boolean;
        message: string;
    };
    updateTraining: {
        isLoading: boolean;
        isSuccess: boolean;
        isError: boolean;
        message: string;
    };
}

const initialState: TrainingState = {
    initiateTraining: {
        isLoading: false,
        isSuccess: false,
        isError: false,
        message: "",
    },
    fetchTrainingData: {
        trainingData: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
        message: "",
    },
    deleteTraining: {
        isLoading: false,
        isSuccess: false,
        isError: false,
        message: "",
    },
    updateTraining: {
        isLoading: false,
        isSuccess: false,
        isError: false,
        message: "",
    },
};

export const initiateTraining = createAsyncThunk("training/initiateTraining", async (payload: any, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const token = state.auth.user.token;
        const response = await trainingService.initiateTraining(payload, token);
        return response;
    } catch (error: any) {
        const message = error.response?.data?.message || error.message || error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchTrainingData = createAsyncThunk("training/fetchTrainingData", async (payload:any, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const token = state.auth.user.token;
        const response = await trainingService.fetchTrainingData(payload,token);
        return response;
    } catch (error: any) {
        const message = error.response?.data?.message || error.message || error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const deleteTraining = createAsyncThunk("training/deleteTraining", async (payload: any, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const token = state.auth.user.token;
        const response = await trainingService.deleteTraining(payload, token);
        return response;
    } catch (error: any) {
        const message = error.response?.data?.message || error.message || error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});

export const updateTraining = createAsyncThunk("training/updateTraining", async (payload: any, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const token = state.auth.user.token;
        const response = await trainingService.updateTraining(payload, token);
        return response;
    } catch (error: any) {
        const message = error.response?.data?.message || error.message || error.toString();
        return thunkAPI.rejectWithValue(message);
    }
});



const trainingSlice = createSlice({
    name: "training",
    initialState,
    reducers: {   
        resetInitiateTraining: (state) => {
            state.initiateTraining = initialState.initiateTraining;
        },
        resetFetchTrainingData: (state) => {
            state.fetchTrainingData = initialState.fetchTrainingData;
        },
        resetDeleteTraining: (state) => {
            state.deleteTraining = initialState.deleteTraining;
        },
        resetUpdateTraining: (state) => {
            state.updateTraining = initialState.updateTraining;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initiateTraining.pending, (state) => {
                state.initiateTraining.isLoading = true;
            })
            .addCase(initiateTraining.fulfilled, (state, action: PayloadAction<any>) => {
                state.initiateTraining.isLoading = false;
                state.initiateTraining.isSuccess = true;
                state.initiateTraining.message = action.payload.message;
            })
            .addCase(initiateTraining.rejected, (state, action: PayloadAction<any>) => {
                state.initiateTraining.isLoading = false;
                state.initiateTraining.isError = true;
                state.initiateTraining.message = action.payload;
            })

            .addCase(fetchTrainingData.pending, (state) => {
                state.fetchTrainingData.isLoading = true;
            })
            .addCase(fetchTrainingData.fulfilled, (state, action: PayloadAction<any>) => {
                state.fetchTrainingData.isLoading = false;
                state.fetchTrainingData.isSuccess = true;
                state.fetchTrainingData.trainingData = action.payload.data.result;
            })
            .addCase(fetchTrainingData.rejected, (state, action: PayloadAction<any>) => {
                state.fetchTrainingData.isLoading = false;
                state.fetchTrainingData.isError = true;
                state.fetchTrainingData.message = action.payload;
            })

            .addCase(deleteTraining.pending, (state) => {
                state.deleteTraining.isLoading = true;
            })
            .addCase(deleteTraining.fulfilled, (state, action: PayloadAction<any>) => {
                state.deleteTraining.isLoading = false;
                state.deleteTraining.isSuccess = true;
                state.deleteTraining.message = action.payload.message;
            })
            .addCase(deleteTraining.rejected, (state, action: PayloadAction<any>) => {
                state.deleteTraining.isLoading = false;
                state.deleteTraining.isError = true;
                state.deleteTraining.message = action.payload;
            })

            .addCase(updateTraining.pending, (state) => {
                state.updateTraining.isLoading = true;
            })
            .addCase(updateTraining.fulfilled, (state, action: PayloadAction<any>) => {
                state.updateTraining.isLoading = false;
                state.updateTraining.isSuccess = true;
                state.updateTraining.message = action.payload.message;
            })
            .addCase(updateTraining.rejected, (state, action: PayloadAction<any>) => {
                state.updateTraining.isLoading = false;
                state.updateTraining.isError = true;
                state.updateTraining.message = action.payload;
            });
    },
});

export const { resetInitiateTraining, resetFetchTrainingData, resetDeleteTraining, resetUpdateTraining } = trainingSlice.actions;
export default trainingSlice.reducer;
